import { api } from './api';

export interface PersonalIssueRow {
  id: string;
  key: string;
  title: string;
  type: 'STORY' | 'TASK' | 'BUG' | 'SUBTASK';
  priority: 'LOWEST' | 'LOW' | 'MEDIUM' | 'HIGH' | 'HIGHEST';
  statusName: string;
  statusCategory: 'TODO' | 'IN_PROGRESS' | 'DONE';
  projectKey: string;
  projectId: string;
  updatedAt: string;
}

export interface PersonalDashboard {
  assignedToMe: PersonalIssueRow[];
  createdByMe: PersonalIssueRow[];
  recentlyUpdated: PersonalIssueRow[];
  upcomingSprintEnds: Array<{
    id: string;
    name: string;
    projectKey: string;
    projectId: string;
    endDate: string;
    state: 'PLANNED' | 'ACTIVE' | 'COMPLETED';
  }>;
}

export async function getPersonalDashboardApi(orgId: string) {
  const { data } = await api.get<PersonalDashboard>(`/orgs/${orgId}/dashboard/personal`);
  return data;
}

export interface ProjectDashboardResponse {
  issueTypeDistribution: Array<{ type: string; count: number }>;
  statusDistribution: Array<{ status: string; category: string; count: number; colorHex: string }>;
  topAssignees: Array<{ userId: string; name: string; openCount: number }>;
  activeSprintId: string | null;
  range: { from: string; to: string };
  totalOpenIssues: number;
  totalIssues: number;
}

export async function getProjectDashboardApi(
  orgId: string,
  projectId: string,
  range: { range: '7d' | '30d' | 'sprint' | 'custom'; from?: string; to?: string },
) {
  const { data } = await api.get<ProjectDashboardResponse>(
    `/orgs/${orgId}/projects/${projectId}/dashboard`,
    { params: range },
  );
  return data;
}

export interface BurndownResponse {
  sprintId: string;
  sprintName: string;
  startDate: string;
  endDate: string | null;
  plannedPoints: number;
  completedPoints: number;
  days: Array<{ date: string; idealRemaining: number; actualRemaining: number }>;
}

export async function getBurndownApi(orgId: string, projectId: string, sprintId: string) {
  const { data } = await api.get<BurndownResponse>(
    `/orgs/${orgId}/projects/${projectId}/sprints/${sprintId}/burndown`,
  );
  return data;
}

export interface SearchResults {
  query: string;
  issues: Array<{
    id: string;
    key: string;
    title: string;
    type: 'STORY' | 'TASK' | 'BUG' | 'SUBTASK';
    projectId: string;
    projectKey: string;
    projectOrgSlug: string | null;
    matchType: 'KEY' | 'TEXT';
  }>;
  projects: Array<{
    id: string;
    key: string;
    name: string;
    description: string | null;
    orgSlug: string | null;
  }>;
  users: Array<{ id: string; name: string; email: string }>;
}

export async function searchApi(orgId: string, q: string, types?: string[]) {
  const params: Record<string, string> = { q };
  if (types?.length) params.types = types.join(',');
  const { data } = await api.get<SearchResults>(`/orgs/${orgId}/search`, { params });
  return data;
}

export interface ProfileRow {
  id: string;
  email: string;
  name: string;
  avatarAttachmentId: string | null;
  timezone: string;
  notifyOnAssign: boolean;
  notifyOnMention: boolean;
  isSuperAdmin: boolean;
  createdAt: string;
}

export async function getProfileApi() {
  const { data } = await api.get<ProfileRow>(`/users/me`);
  return data;
}

export interface UpdateProfileBody {
  name?: string | undefined;
  avatarAttachmentId?: string | null | undefined;
  timezone?: string | undefined;
  notifyOnAssign?: boolean | undefined;
  notifyOnMention?: boolean | undefined;
}

export async function updateProfileApi(body: UpdateProfileBody) {
  const { data } = await api.patch<ProfileRow>(`/users/me`, body);
  return data;
}

export async function changePasswordApi(body: {
  currentPassword: string;
  newPassword: string;
  refreshToken?: string;
}) {
  const { data } = await api.post<{ invalidatedSessions: number }>(`/users/me/password`, body);
  return data;
}
